import type { FollowUpKind } from './followUp'

// Enums matching backend
export enum SurveyType {
  Volunteer = 0,
  Action = 1,
  Issue = 2,
  Candidate = 3,
  Party = 4,
}

export enum AppliesTo {
  People = 0,
  Organizations = 1,
  Both = 2,
}

export enum AccessLevel {
  AllCommittees = 0,
  RestrictedCommittees = 1,
}

// Survey Group DTOs
export interface SurveyGroupSummaryDto {
  id: string
  label: string
  description?: string | null
  questionCount: number
  createdAt: string
  createdByName: string
}

export interface SurveyGroupDetailDto {
  id: string
  label: string
  description?: string | null
  createdAt: string
  updatedAt: string
  createdBy: string
  createdByName: string
  questions: SurveyQuestionSummaryDto[]
}

export interface SaveSurveyGroupRequest {
  label: string
  description?: string | null
}

// Survey Question DTOs
export interface SurveyQuestionSummaryDto {
  id: string
  label: string
  labelSpanish?: string | null
  question: string
  type: SurveyType
  cycle: number
  appliesTo: AppliesTo
  surveyGroupId?: string | null
  surveyGroupLabel?: string | null
  isActive: boolean
  accessLevel: AccessLevel
  answerCount: number
  createdAt: string
  createdByName: string
  starterKey?: string | null
}

export interface SurveyQuestionDetailDto {
  id: string
  label: string
  labelSpanish?: string | null
  question: string
  questionSpanish?: string | null
  type: SurveyType
  cycle: number
  appliesTo: AppliesTo
  surveyGroupId?: string | null
  surveyGroupLabel?: string | null
  isActive: boolean
  accessLevel: AccessLevel
  ownerCommitteeId: string
  createdAt: string
  updatedAt: string
  createdBy: string
  createdByName: string
  starterKey?: string | null
  answers: AnswerDto[]
}

export interface SaveSurveyQuestionRequest {
  label: string
  labelSpanish?: string | null
  question: string
  questionSpanish?: string | null
  type: SurveyType
  cycle: number
  appliesTo: AppliesTo
  surveyGroupId?: string | null
  isActive?: boolean
  accessLevel: AccessLevel
  answers: SaveAnswerRequest[]
}

// Answer DTOs
export interface AnswerDto {
  id: string
  answerText: string
  answerTextSpanish?: string | null
  shortCode?: string | null
  displayOrder: number
  isActive: boolean
  followUpKind?: FollowUpKind | null
  followUpCustomKindId?: string | null
}

export interface SaveAnswerRequest {
  id?: string | null  // Null for new, populated for updates
  answerText: string
  answerTextSpanish?: string | null
  shortCode?: string | null
  displayOrder: number
  followUpKind?: FollowUpKind | null
  followUpCustomKindId?: string | null
}

// Helper constants
export const SURVEY_TYPE_LABELS: Record<SurveyType, string> = {
  [SurveyType.Volunteer]: 'Volunteer',
  [SurveyType.Action]: 'Action',
  [SurveyType.Issue]: 'Issue',
  [SurveyType.Candidate]: 'Candidate',
  [SurveyType.Party]: 'Party',
}

export const APPLIES_TO_LABELS: Record<AppliesTo, string> = {
  [AppliesTo.People]: 'People',
  [AppliesTo.Organizations]: 'Organizations',
  [AppliesTo.Both]: 'Both',
}

export const ACCESS_LEVEL_LABELS: Record<AccessLevel, string> = {
  [AccessLevel.AllCommittees]: 'All Committees',
  [AccessLevel.RestrictedCommittees]: 'Restricted Committees',
}

// Search DTOs
export interface SurveyQuestionSearchRequest {
  label?: string
  questionText?: string
  type?: SurveyType
  cycle?: number
  surveyGroupId?: string
  isActive?: boolean
  page: number
  pageSize: number
  sortBy?: string
  sortDescending?: boolean
}

export interface PaginatedResponse<T> {
  items: T[]
  totalCount: number
  page: number
  pageSize: number
  totalPages: number
  hasNextPage: boolean
  hasPreviousPage: boolean
}
